import * as Yup from 'yup';
import { DEALER_ADMIN } from 'src/utils/enums/user-store-actions.enum';
import { DealerManagementService } from 'src/services/dealer-management.service';
import { RadioButtonOption } from 'src/components/ui/radioButton';
import Spinner from 'src/components/ui/spinner';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import { NUMBER_ONE, NUMBER_ZERO } from 'src/utils/constants';
import React, { useEffect, useState } from 'react';
import './styles.scss';

export enum Types {
    UserId = 'userId',
    LastName = 'lastName',
    DealerNumber = 'dealerNumber',
}

const RenderRadioInput = React.memo(
    ({
        label,
        type,
        values,
        handleChange,
        handleBlur,
        touched,
        errors,
        isSubmitting,
        handleSearchTypeChange,
        maxlength = 524288,
    }: {
        label: string;
        type: string;
        values: any;
        handleChange: any;
        handleBlur: any;
        touched: any;
        errors: any;
        isSubmitting: boolean;
        handleSearchTypeChange: any;
        maxlength?: number;
    }) => (
        <div className='c-input radioTextInputCombo'>
            <RadioButtonOption
                parentName={`typeRadio-${type}`}
                option={type}
                onSelect={() => handleSearchTypeChange(type)}
                selected={values.searchType === type}
                label={label}
            />
            <div className='input-text-wrapper'>
                <input
                    className='c-input__input'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type='text'
                    value={values.searchType === type ? values.searchValue : ''}
                    disabled={values.searchType !== type}
                    name='searchValue'
                    data-testid={`${type}Input`}
                    maxLength={maxlength}
                />
                {values.searchType === type &&
                    touched.searchValue &&
                    !isSubmitting &&
                    errors.searchValue && (
                        <>
                            <br />
                            <div className='error-message'>
                                {errors.searchValue}
                            </div>
                        </>
                    )}
            </div>
        </div>
    ),
);

RenderRadioInput.displayName = 'RenderRadioInput';

const DealerSecurityMaintenance = () => {
    const [isLoading, setLoading] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [isSuccess, setIsSuccess] = useState<null | boolean>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch({
            type: DEALER_ADMIN.DEALER_ADMIN_USER_SEARCH,
            payload: {
                searchType: Types.UserId,
                searchValue: '',
                users: [],
            },
        });
    }, []);

    const validationSchema = Yup.object({
        searchValue: Yup.string()
            .required('This field is required')
            .when('searchType', {
                is: 'userId',
                then: Yup.string().matches(
                    /^([a-zA-Z0-9_\u00C0-\u024F\-.]+)@([a-zA-Z0-9_\u00C0-\u024F\-.]+)\.([a-zA-Z\u00C0-\u024F]{2,63})$/i,
                    'Please enter a valid User ID (e.g., user@example.com).',
                ),
            })
            .when('searchType', {
                is: 'lastName',
                then: Yup.string().matches(
                    /^[A-Za-z '&,-]{1,30}$/,
                    'Last Name is missing or invalid.',
                ),
            })
            .when('searchType', {
                is: 'dealerNumber',
                then: Yup.string().matches(
                    /^[A-Za-z0-9]{6}$/,
                    'Please enter a valid Dealer Number.',
                ),
            }),
    });
    const formik = useFormik({
        initialValues: {
            searchType: 'userId',
            searchValue: '',
        },
        validationSchema,
        validateOnChange: false,
        onSubmit: async (values) => {
            setShowMessage(false);
            setLoading(true);
            const service = new DealerManagementService();

            try {
                const res = await service.getDealersBySearchType(
                    values.searchType,
                    values.searchValue,
                );

                if (
                    (res.data && res.data.length > NUMBER_ZERO) ||
                    values.searchType === Types.DealerNumber
                ) {
                    setIsSuccess(true);
                    dispatch({
                        type: DEALER_ADMIN.DEALER_ADMIN_USER_SEARCH,
                        payload: {
                            searchType: values.searchType,
                            searchValue: values.searchValue,
                            users: res?.data || [],
                        },
                    });
                    if (values.searchType === Types.DealerNumber) {
                        const dealerGroupsRes = await service.getDealerGroups(
                            values.searchValue,
                        );
                        const userPermissions: any[] = [];
                        const associations: any[] = [];

                        if (dealerGroupsRes?.data?.length) {
                            dealerGroupsRes.data
                                ?.flatMap((group: any) => group.dealers)
                                .forEach((dealer: any) => {
                                    const dealerId = dealer.dealerNumber;

                                    dealer?.fspPermissions?.forEach(
                                        (permission: any) => {
                                            userPermissions.push({
                                                dealerId,
                                                permission,
                                                country: dealer.country,
                                                manufacturerCode:
                                                    dealer.manufacturerCode,
                                            });
                                        },
                                    );

                                    associations.push({ dealerId });
                                });
                            navigate(
                                '/dashboard/systemadmin/dealer-security-maintenance/search',
                                {
                                    state: {
                                        associations,
                                        permissions: userPermissions,
                                    },
                                },
                            );
                            return;
                        } else {
                            setShowMessage(true);
                            setErrorMessage(
                                'No results found. Please verify your search criteria and try again.',
                            );
                            setIsSuccess(false);
                        }
                    } else
                        navigate(
                            '/dashboard/systemadmin/dealer-security-maintenance/search',
                        );
                } else {
                    setShowMessage(true);
                    setErrorMessage(
                        'No results found. Please verify your search criteria and try again.',
                    );
                    setIsSuccess(false);
                }
            } catch (e: any) {
                setShowMessage(true);
                setIsSuccess(false);
                if (
                    e.response &&
                    e.response.data &&
                    e.response.data.error &&
                    e.response.data.error.message
                ) {
                    setErrorMessage(e.response.data.error.message);
                } else {
                    setErrorMessage(
                        'Something went wrong. Please try again later.',
                    );
                }
                setIsSuccess(false);
            } finally {
                setLoading(false);
            }
        },
    });

    const {
        values,
        setFieldValue,
        errors,
        touched,
        handleSubmit,
        handleChange,
        handleBlur,
        isSubmitting,
        setFieldError,
    } = formik;

    const handleClear = () => {
        formik.resetForm();
    };

    const handleSearchTypeChange = (type: string) => {
        setFieldValue('searchType', type);
        setFieldValue('searchValue', '');
        setFieldError('searchValue', '');
    };

    return (
        <div className='dashboard-content-wrapper'>
            <div className='dashboard-content-container'>
                <div className='search-wrapper'>
                    <section className='o-content-section'>
                        {showMessage && (
                            <Notification
                                content={errorMessage}
                                type={
                                    isSuccess
                                        ? NotificationType.SUCCESS
                                        : NotificationType.ERROR
                                }
                                visibility={showMessage}
                                setVisibility={setShowMessage}
                                withoutClose={false}
                            />
                        )}
                        <div>
                            <h5>
                                <span className='c-section-heading__title-text'>
                                    Dealer Security Maintenance Search
                                </span>
                            </h5>
                            <section className='c-form-section'>
                                <div className='c-form-section__content'>
                                    <div className='o-fieldset__row u-mt-large u-mb-large'>
                                        <div className='o-layout'>
                                            <form
                                                className='o-layout__item u-1/1 u-1/1@xs'
                                                onSubmit={handleSubmit}
                                            >
                                                <div className='o-layout__item u-1/1 u-1/1@xs'>
                                                    <div className='descTxtWrapper'>
                                                        <p className='mb-10'>
                                                            Select a search
                                                            option, enter your
                                                            criteria, and click
                                                            &quot;Submit.&quot;
                                                        </p>
                                                        <p>
                                                            <strong>
                                                                Search tips:
                                                            </strong>
                                                        </p>

                                                        <ul>
                                                            <li>
                                                                <strong>
                                                                    User ID:
                                                                </strong>
                                                                &nbsp;Enter the
                                                                exact User ID
                                                            </li>
                                                            <li>
                                                                <strong>
                                                                    Last Name:
                                                                </strong>
                                                                &nbsp;Enter full
                                                                or partial last
                                                                name
                                                            </li>
                                                            <li>
                                                                <strong>
                                                                    Dealer
                                                                    Number:
                                                                </strong>
                                                                &nbsp;Enter the
                                                                exact Dealer
                                                                Number to find
                                                                all users for
                                                                that dealer or
                                                                to maintain a
                                                                Dealer Group
                                                            </li>
                                                        </ul>
                                                        <p>
                                                            Click
                                                            &quot;Clear&quot; to
                                                            reset the search and
                                                            start over.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className='o-layout__item u-1/1 u-1/1@xs u-mt-small'>
                                                    <div className='c-form-field'>
                                                        <div className='c-form-field__box'>
                                                            <RenderRadioInput
                                                                label='User ID:'
                                                                maxlength={50}
                                                                type={
                                                                    Types.UserId
                                                                }
                                                                values={values}
                                                                handleChange={
                                                                    handleChange
                                                                }
                                                                handleBlur={
                                                                    handleBlur
                                                                }
                                                                touched={
                                                                    touched
                                                                }
                                                                errors={errors}
                                                                isSubmitting={
                                                                    isSubmitting
                                                                }
                                                                handleSearchTypeChange={
                                                                    handleSearchTypeChange
                                                                }
                                                            />
                                                            <RenderRadioInput
                                                                label='Last Name:'
                                                                maxlength={30}
                                                                type={
                                                                    Types.LastName
                                                                }
                                                                values={values}
                                                                handleChange={
                                                                    handleChange
                                                                }
                                                                handleBlur={
                                                                    handleBlur
                                                                }
                                                                touched={
                                                                    touched
                                                                }
                                                                errors={errors}
                                                                isSubmitting={
                                                                    isSubmitting
                                                                }
                                                                handleSearchTypeChange={
                                                                    handleSearchTypeChange
                                                                }
                                                            />
                                                            <RenderRadioInput
                                                                label='Dealer Number:'
                                                                maxlength={6}
                                                                type={
                                                                    Types.DealerNumber
                                                                }
                                                                values={values}
                                                                handleChange={(
                                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                                ) => {
                                                                    setFieldValue(
                                                                        'searchValue',
                                                                        e.target.value.toUpperCase(),
                                                                    );
                                                                }}
                                                                handleBlur={
                                                                    handleBlur
                                                                }
                                                                touched={
                                                                    touched
                                                                }
                                                                errors={errors}
                                                                isSubmitting={
                                                                    isSubmitting
                                                                }
                                                                handleSearchTypeChange={
                                                                    handleSearchTypeChange
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className='o-layout__item u-1/1 u-1/1@xs'>
                                                    <div className='o-fieldset__row button-group'>
                                                        <button
                                                            className='c-btn--secondary c-btn c-btn--small mr-2'
                                                            type='button'
                                                            onClick={
                                                                handleClear
                                                            }
                                                        >
                                                            Clear
                                                        </button>
                                                        <button
                                                            className={`c-btn ${
                                                                values
                                                                    .searchValue
                                                                    .length <
                                                                NUMBER_ONE
                                                                    ? 'btn-secondary'
                                                                    : ''
                                                            }`}
                                                            type='submit'
                                                            onClick={() =>
                                                                handleSubmit()
                                                            }
                                                        >
                                                            <span className='c-btn__text'>
                                                                {isLoading ? (
                                                                    <Spinner />
                                                                ) : (
                                                                    'Submit'
                                                                )}
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default DealerSecurityMaintenance;
